import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, BookOpen, Sparkles, ExternalLink, Loader2, Play, FileText, Folder, LayoutGrid } from "lucide-react";
import api from "../lib/api";
import { FormRecurso, TIPOS } from "../components/descargas/FormRecurso";
import { TarjetaRecurso, Visor, ficheroUrl, sePuedeVer } from "../components/descargas/TarjetaRecurso";
import { MisNotas } from "../components/descargas/MisNotas";

const ICON = { video: Play, tutorial: BookOpen, manual: FileText, otros: Folder };

function Destacado({ r, tono, onVer }) {
  const dark = tono === "recomendado";
  const accion = r.es_enlace ? { href: r.url, target: "_blank", rel: "noopener noreferrer" } : { href: ficheroUrl(r, true) };
  return (
    <div className={"rounded-2xl p-5 flex items-center gap-4 flex-wrap " + (dark ? "bg-slate-900 text-white shadow-lg" : "bg-emerald-50 border border-emerald-200")} data-testid={`destacado-${tono}`}>
      {dark && <div className="h-11 w-11 rounded-xl bg-amber-400/15 text-amber-300 flex items-center justify-center shrink-0"><BookOpen size={20} /></div>}
      <div className="flex-1 min-w-[220px]">
        <div className={"text-[10px] font-extrabold uppercase tracking-[0.2em] " + (dark ? "text-amber-300" : "text-emerald-700")}>{dark ? "Recomendado" : "Novedades"}</div>
        <div className="font-display text-lg font-bold mt-0.5">{r.titulo}</div>
        {r.descripcion && <p className={"text-sm mt-1 " + (dark ? "text-slate-300" : "text-emerald-900/70")}>{r.descripcion}</p>}
      </div>
      <div className="flex gap-2">
        {sePuedeVer(r) && <button onClick={() => onVer(r)} className={"btn " + (dark ? "bg-white/10 text-white hover:bg-white/20" : "btn-outline")} data-testid={`destacado-${tono}-ver`}><Play size={14} /> Ver</button>}
        <a {...accion} className={"btn font-bold " + (dark ? "bg-amber-400 text-slate-900 hover:bg-amber-300" : "bg-emerald-700 text-white hover:bg-emerald-800")} data-testid={`destacado-${tono}-descargar`}>
          {r.es_enlace ? <ExternalLink size={14} /> : <Download size={14} />} {r.es_enlace ? "Abrir" : "Descargar"}
        </a>
      </div>
    </div>
  );
}

function Consultoria() {
  return (
    <aside className="lg:sticky lg:top-20 self-start" data-testid="descargas-consultoria">
      <a href="tel:+34609467788" className="block rounded-3xl overflow-hidden shadow-lg transition-transform hover:-translate-y-0.5" data-testid="descargas-llamar"
         title="Llamar a Pedro · 609467788">
        <img src="/img/archiduque-consultoria.jpg" alt="Consultoría especializada · Tu farmacia, más rentable · Archiduque Consulting · Pedro Domenech · Consultor de Farmacias · 609467788"
             className="w-full h-auto block" />
      </a>
      <p className="text-xs text-slate-500 text-center mt-3">¿Necesitas algo concreto? Escríbenos a <a className="underline" href="mailto:pedro@archiduquecarlos.com">pedro@archiduquecarlos.com</a></p>
    </aside>
  );
}

export default function Descargas() {
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [form, setForm] = useState(null);
  const [ver, setVer] = useState(null);
  const load = () => api.get("/descargas").then((r) => setData(r.data)).catch(() => setData({ recursos: [], admin: false }));
  useEffect(() => {
    load();
    api.post("/descargas/visto").then(() => window.dispatchEvent(new Event("descargas-vistas"))).catch(() => {});
  }, []);

  const borrar = async (r) => {
    if (!window.confirm(`¿Borrar «${r.titulo}»? Los clientes dejarán de verlo.`)) return;
    await api.delete(`/descargas/${r.id}`);
    toast.success("Recurso borrado");
    load();
  };

  if (!data) return <div className="p-8 text-slate-500 flex gap-2"><Loader2 size={16} className="animate-spin" /> Cargando…</div>;
  const rec = data.recursos;
  const recomendado = rec.find((r) => r.destacado === "recomendado" && r.completo);
  const novedades = rec.find((r) => r.destacado === "novedades" && r.completo);
  const lista = filtro === "todos" ? rec : rec.filter((r) => r.tipo === filtro);
  const chips = [["todos", "Todos", LayoutGrid], ...TIPOS.map(([k, l]) => [k, l, ICON[k]])];

  return (
    <div className="p-4 sm:p-8 fade-in" data-testid="descargas-page">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 max-w-7xl">
        <div className="min-w-0 space-y-5">
          {recomendado && <Destacado r={recomendado} tono="recomendado" onVer={setVer} />}
          {novedades && <Destacado r={novedades} tono="novedades" onVer={setVer} />}
          <div className="pt-2">
            <img src="/img/farmanaliza-descargas.jpg" alt="Farmanaliza · Descargas y formación: recursos prácticos para mejorar tu farmacia"
                 className="w-full rounded-2xl shadow-sm h-auto" data-testid="descargas-banner" />
            <h1 className="sr-only">Descargas y formación</h1>
            <p className="text-slate-500 mt-3 text-sm">Vídeos de formación, tutoriales y manuales para sacar el máximo partido a Farmanaliza.</p>
          </div>
          {data.admin && (
            <button className="btn btn-primary rounded-full" onClick={() => setForm({})} data-testid="recurso-anadir"><Plus size={14} /> Añadir recurso</button>
          )}
          <div className="flex gap-2 flex-wrap" data-testid="descargas-filtros">
            {chips.map(([k, l, I]) => (
              <button key={k} onClick={() => setFiltro(k)} data-testid={`descargas-filtro-${k}`}
                      className={"px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors " + (filtro === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400")}>
                <I size={12} /> {l} · {k === "todos" ? rec.length : rec.filter((r) => r.tipo === k).length}
              </button>
            ))}
          </div>
          {!lista.length && <div className="card p-8 text-center text-slate-500" data-testid="descargas-vacio">Todavía no hay recursos en esta categoría.</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {lista.map((r) => (
              <div key={r.id} className="relative">
                {data.admin && !r.completo && <span className="absolute top-2 left-2 z-10 text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Subida incompleta</span>}
                <TarjetaRecurso r={r} admin={data.admin} onVer={setVer} onEditar={(x) => setForm({ inicial: x })} onBorrar={borrar} />
              </div>
            ))}
          </div>
          <MisNotas />
        </div>
        <Consultoria />
      </div>
      {form && <FormRecurso inicial={form.inicial} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {ver && <Visor r={ver} onClose={() => setVer(null)} />}
    </div>
  );
}
